import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2 as Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import * as React from 'react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SearchLG } from 'untitledui-js-base';
import { SeznamAutocomplete } from '../../types/external/seznamAutocomplete';
import { findCountryId } from '../../utils/countryMapping';
import { extractBirthDateFromBirthNumber } from '../../utils/extractBirthDateFromBirthNumber';
import { handleEnterAsTab } from '../../utils/handleEnterAsTab';
import { BoxWithHeader } from '../common';
import {
  Accident,
  AccidentExistingUserResponse,
  AccidentUserType,
  AddNewAccountRequest,
  AresResponse,
  RegistryIdName,
  UserEmailAccountResponse
} from '../types/accident';
import { AddNewAccountDialog } from './components/AddNewAccountDialog';
import { BirthNumberFields } from './components/BirthNumberFields';
import { ParticipantFields } from './components/ParticipantFieldsts';
import { useSameAsHiding } from './hooks/useSameAsHiding';
import { initializeSameAsValues } from './utils/sameAsCheckers';
import { applyAresData, applyExistingData, toFormValues } from './utils/utils';

// ─── Types ────────────────────────────────────────────────────────────────────
export type SameAsValue = 'user' | 'operator' | 'driver' | 'none';
export type UserPrefix = 'user' | 'operator';

interface AccidentCreatingStepInformationsProps {
  onSubmit: (data: Accident) => void;
  formRef: React.RefObject<HTMLFormElement>;
  accident?: Accident;
  contactSameAsOwner?: SameAsValue;
  onChangeContactSameAsOwner?: (value: SameAsValue) => void;
  driverSameAsOwner?: SameAsValue;
  onChangeDriverSameAsOwner?: (value: SameAsValue) => void;
  operatorSameAsOwner?: boolean;
  onChangeOperatorSameAsOwner?: (value: boolean) => void;
  onAresClick?: (vatNumber: string, isOperator?: boolean) => void;
  aresData?: AresResponse;
  operatorAresData?: AresResponse;
  isAresDataLoading?: boolean;
  userType?: AccidentUserType;
  handleSetUserType?: (value: AccidentUserType) => void;
  operatorType?: AccidentUserType;
  handleSetOperatorType?: (value: AccidentUserType) => void;
  onAutocompleteSearch?: (value: string, isOperator?: boolean) => void;
  autocompleteResults?: SeznamAutocomplete[];
  operatorAutocompleteResults?: SeznamAutocomplete[];
  isLoadingAddressData?: boolean;
  countryList?: RegistryIdName[];
  onGetExistingDataClick?: (identification: string, isOperator?: boolean) => void;
  existingUserData?: AccidentExistingUserResponse;
  existingOperatorData?: AccidentExistingUserResponse;
  handleGetAccountEmail?: (email?: string) => void;
  accountResult: UserEmailAccountResponse | boolean | undefined;
  setAddNewAccountDialogOpen: (open: boolean) => void;
  addNewAccountDialogOpen: boolean;
  isCreatingAccount: boolean;
  handleCreateAccount: (formData: AddNewAccountRequest) => void;
}

export const AccidentCreatingStepCustomerInformations = ({
  onSubmit,
  formRef,
  accident,
  contactSameAsOwner,
  onChangeContactSameAsOwner,
  driverSameAsOwner,
  onChangeDriverSameAsOwner,
  operatorSameAsOwner,
  onChangeOperatorSameAsOwner,
  onAresClick,
  aresData,
  operatorAresData,
  isAresDataLoading,
  userType,
  handleSetUserType,
  operatorType,
  handleSetOperatorType,
  onAutocompleteSearch,
  autocompleteResults,
  operatorAutocompleteResults,
  isLoadingAddressData,
  countryList,
  onGetExistingDataClick,
  existingUserData,
  existingOperatorData,
  addNewAccountDialogOpen,
  setAddNewAccountDialogOpen,
  handleGetAccountEmail,
  accountResult,
  handleCreateAccount,
  isCreatingAccount
}: AccidentCreatingStepInformationsProps) => {
  const { handleSubmit, setValue, control, reset, watch } = useForm<Accident>({
    shouldUnregister: false,
    defaultValues: accident
  });

  const isCompany = userType === AccidentUserType.Company || userType === AccidentUserType.BusinessMan;
  const isOperatorCompany = operatorType === AccidentUserType.Company || operatorType === AccidentUserType.BusinessMan;

  // Řidič ani kontaktní osoba nemůžou být firma (pouze právnická osoba)
  const isUserCompanyOnly = userType === AccidentUserType.Company;
  const isOperatorCompanyOnly = operatorType === AccidentUserType.Company;

  // Podmínky pro řidiče
  const shouldHideUserOptionForDriver = isUserCompanyOnly;
  // Skrýt možnost "operator", pokud je operátor stejný jako majitel NEBO pokud je operátor firma
  const shouldHideOperatorOptionForDriver = operatorSameAsOwner || isOperatorCompanyOnly;

  // Podmínky pro kontaktní osobu (stejné jako pro řidiče)
  const shouldHideUserOptionForContact = isUserCompanyOnly;
  // Skrýt možnost "operator", pokud je operátor stejný jako majitel NEBO pokud je operátor firma
  const shouldHideOperatorOptionForContact = operatorSameAsOwner || isOperatorCompanyOnly;
  // Skrýt možnost "driver", pokud je řidič firma (což by znamenalo, že je stejný jako majitel/operátor který je firma)
  const shouldHideDriverOptionForContact =
    (driverSameAsOwner === 'user' && isUserCompanyOnly) || (driverSameAsOwner === 'operator' && isOperatorCompanyOnly);

  // Sledované hodnoty
  const ico = watch('user.company.companyIdNum');
  const operatorIco = watch('operator.company.companyIdNum');
  const birthNumber = watch('user.birthNumber');
  const operatorBirthNumber = watch('operator.birthNumber');
  const driverBirthNumber = watch('driver.birthNumber');
  const contactBirthNumber = watch('contactPerson.birthNumber');
  const notificationEmail = watch('notification.email');
  const notificationPhone = watch('notification.phone');

  // Reset při změně accident prop
  useEffect(() => {
    if (accident) {
      const formValues = toFormValues(accident);
      const sameAsValues = initializeSameAsValues(accident);

      formValues.operator = {
        ...(formValues.operator ?? {}),
        equalTo: sameAsValues.operatorSameAsOwner ? 'user' : 'none'
      } as any;

      formValues.driver = {
        ...(formValues.driver ?? {}),
        equalTo: sameAsValues.driverSameAsOwner
      } as any;

      formValues.contactPerson = {
        ...(formValues.contactPerson ?? {}),
        equalTo: sameAsValues.contactSameAsOwner
      } as any;

      reset(formValues);
    }
  }, [accident, reset]);

  // ARES data
  useEffect(() => {
    applyAresData('user', aresData, setValue, userType);
  }, [aresData, setValue, userType]);
  useEffect(() => {
    applyAresData('operator', operatorAresData, setValue, operatorType);
  }, [operatorAresData, setValue, operatorType]);

  // Existující data ze systému
  useEffect(() => {
    applyExistingData('user', existingUserData, isCompany, setValue);
  }, [existingUserData, isCompany, setValue]);
  useEffect(() => {
    applyExistingData('operator', existingOperatorData, isOperatorCompany, setValue);
  }, [existingOperatorData, isOperatorCompany, setValue]);

  useSameAsHiding(
    driverSameAsOwner,
    onChangeDriverSameAsOwner,
    shouldHideUserOptionForDriver,
    shouldHideOperatorOptionForDriver
  );

  useSameAsHiding(
    contactSameAsOwner,
    onChangeContactSameAsOwner,
    shouldHideUserOptionForContact,
    shouldHideOperatorOptionForContact
  );

  // Automatické odvození data narození z rodného čísla
  useEffect(() => {
    const pairs: Array<[string | undefined, 'user.birthDate' | 'operator.birthDate' | 'driver.birthDate']> = [
      [birthNumber, 'user.birthDate'],
      [operatorBirthNumber, 'operator.birthDate'],
      [driverBirthNumber, 'driver.birthDate']
    ];
    pairs.forEach(([bn, field]) => {
      const date = extractBirthDateFromBirthNumber(bn ?? '');
      if (date) setValue(field, date);
    });
  }, [birthNumber, operatorBirthNumber, driverBirthNumber, setValue]);

  // Synchronizace typu uživatele do RHF
  useEffect(() => {
    setValue('user.type.name' as any, userType ?? AccidentUserType.Person);
  }, [userType, setValue]);

  useEffect(() => {
    if (!operatorSameAsOwner) {
      setValue('operator.type.name' as any, operatorType ?? AccidentUserType.Person);
    }
  }, [operatorType, operatorSameAsOwner, setValue]);

  // Synchronizace equalTo hodnot do RHF
  useEffect(() => {
    const equalToValue = contactSameAsOwner ?? 'none';
    const currentContact = watch('contactPerson');
    if (!currentContact || typeof currentContact !== 'object') {
      setValue('contactPerson' as any, { equalTo: equalToValue });
    } else {
      setValue('contactPerson.equalTo' as any, equalToValue);
    }
  }, [contactSameAsOwner, setValue, watch]);

  useEffect(() => {
    const equalToValue = driverSameAsOwner ?? 'none';
    const currentDriver = watch('driver');
    if (!currentDriver || typeof currentDriver !== 'object') {
      setValue('driver' as any, { equalTo: equalToValue });
    } else {
      setValue('driver.equalTo' as any, equalToValue);
    }
  }, [driverSameAsOwner, setValue, watch]);

  useEffect(() => {
    const equalToValue = operatorSameAsOwner === false ? 'none' : 'user';
    // Ujistit se, že operator objekt existuje před nastavením equalTo
    const currentOperator = watch('operator');
    if (!currentOperator || typeof currentOperator !== 'object' || Array.isArray(currentOperator)) {
      setValue('operator' as any, { equalTo: equalToValue });
    } else {
      setValue('operator.equalTo' as any, equalToValue);
    }
  }, [operatorSameAsOwner, setValue, watch]);

  // Pomocné handlery pro vyhledání existujícího záznamu
  const handleSearchForExistingUser = (isOperator: boolean) => {
    const targetIsCompany = isOperator ? isOperatorCompany : isCompany;
    const identification = targetIsCompany
      ? isOperator
        ? operatorIco
        : ico
      : (isOperator ? operatorBirthNumber : birthNumber) ?? '';
    onGetExistingDataClick?.(identification, isOperator);
  };

  // Pomocný handler pro výběr adresy z autocomplete
  const handleAutocompleteSelect = (value: SeznamAutocomplete, isOperator: boolean) => {
    const prefix: UserPrefix = isOperator ? 'operator' : 'user';
    const find = (type: string) => value.data.regionalStructure?.find((item) => item.type === type)?.name ?? '';

    const municipality = find('regional.municipality');
    const municipalityPart = find('regional.municipality_part');
    const cityParts = [municipality];
    if (municipalityPart && municipalityPart !== municipality) cityParts.push(municipalityPart);

    setValue(`${prefix}.address.street` as any, find('regional.street'));
    setValue(`${prefix}.address.houseNum` as any, find('regional.address'));
    setValue(`${prefix}.address.city` as any, cityParts.filter(Boolean).join(', '));
    setValue(`${prefix}.address.zip` as any, value.data.zip);

    const countryId = findCountryId(find('regional.country'), countryList);
    if (countryId !== undefined) setValue(`${prefix}.address.countryId` as any, countryId);
  };

  return (
    <BoxWithHeader title="Údaje o účastnících" mt={2} style={{ height: 'auto' }}>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Grid container spacing={5} mt={1}>
          {/* ── Majitel ── */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Typography variant="h6">Majitel vozidla</Typography>
            <Box mt={2}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Typ osoby</FormLabel>
                <RadioGroup
                  row
                  value={userType || AccidentUserType.Person}
                  onChange={(e) => handleSetUserType?.(e.target.value as AccidentUserType)}
                >
                  <FormControlLabel value={AccidentUserType.Person} control={<Radio />} label="Fyzická osoba" />
                  <FormControlLabel value={AccidentUserType.Company} control={<Radio />} label="Firma" />
                  <FormControlLabel value={AccidentUserType.BusinessMan} control={<Radio />} label="Podnikatel" />
                </RadioGroup>
              </FormControl>

              <ParticipantFields
                prefix="user"
                control={control}
                userType={userType ?? AccidentUserType.Person}
                ico={ico}
                birthNumber={birthNumber}
                isAresDataLoading={isAresDataLoading}
                countryList={countryList}
                autocompleteResults={autocompleteResults ?? []}
                onAresClick={() => onAresClick?.(ico, false)}
                onSystemSearch={() => handleSearchForExistingUser(false)}
                onBirthNumberSearch={() => handleSearchForExistingUser(false)}
                onAutocompleteSearch={(v) => onAutocompleteSearch?.(v, false)}
                onAutocompleteSelect={(v) => handleAutocompleteSelect(v, false)}
                isLoadingAddressData={isLoadingAddressData}
              />
            </Box>
          </Grid>

          {/* ── Provozovatel ── */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Typography variant="h6">Provozovatel vozidla</Typography>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={operatorSameAsOwner}
                    onChange={(_, checked) => {
                      onChangeOperatorSameAsOwner?.(checked);
                    }}
                  />
                }
                label="Stejný jako majitel"
              />

              {!operatorSameAsOwner && (
                <Box>
                  <FormControl component="fieldset" sx={{ mt: 2 }}>
                    <FormLabel component="legend">Typ osoby</FormLabel>
                    <RadioGroup
                      row
                      value={operatorType || AccidentUserType.Person}
                      onChange={(e) => handleSetOperatorType?.(e.target.value as AccidentUserType)}
                    >
                      <FormControlLabel value={AccidentUserType.Person} control={<Radio />} label="Fyzická osoba" />
                      <FormControlLabel value={AccidentUserType.Company} control={<Radio />} label="Firma" />
                      <FormControlLabel value={AccidentUserType.BusinessMan} control={<Radio />} label="Podnikatel" />
                    </RadioGroup>
                  </FormControl>

                  <ParticipantFields
                    prefix="operator"
                    control={control}
                    userType={operatorType ?? AccidentUserType.Person}
                    ico={operatorIco}
                    birthNumber={operatorBirthNumber}
                    isAresDataLoading={isAresDataLoading}
                    countryList={countryList}
                    autocompleteResults={operatorAutocompleteResults ?? []}
                    onAresClick={() => onAresClick?.(operatorIco, true)}
                    onSystemSearch={() => handleSearchForExistingUser(true)}
                    onBirthNumberSearch={() => handleSearchForExistingUser(true)}
                    onAutocompleteSearch={(v) => onAutocompleteSearch?.(v, true)}
                    onAutocompleteSelect={(v) => handleAutocompleteSelect(v, true)}
                    isLoadingAddressData={isLoadingAddressData}
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {/* ── Řidič ── */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Typography variant="h6">Řidič vozidla</Typography>
            <Box mt={2}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={driverSameAsOwner || 'none'}
                  onChange={(e) => {
                    const newValue = e.target.value as SameAsValue;
                    onChangeDriverSameAsOwner?.(newValue);
                  }}
                >
                  {!shouldHideUserOptionForDriver && (
                    <FormControlLabel value="user" control={<Radio />} label="Stejný jako majitel" />
                  )}
                  {!shouldHideOperatorOptionForDriver && (
                    <FormControlLabel value="operator" control={<Radio />} label="Stejný jako provozovatel" />
                  )}
                  <FormControlLabel value="none" control={<Radio />} label="Jiný" />
                </RadioGroup>
              </FormControl>

              {driverSameAsOwner === 'none' && (
                <Stack gap={2} mt={2}>
                  <Grid container spacing={2}>
                    <Grid size={3}>
                      <Controller
                        control={control}
                        name="driver.degree"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            label="Titul"
                            color="secondary"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            onKeyDown={handleEnterAsTab}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={9}>
                      <Controller
                        control={control}
                        name="driver.name"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            label="Jméno"
                            color="secondary"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            onKeyDown={handleEnterAsTab}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Controller
                    control={control}
                    name="driver.surname"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label="Příjmení"
                        color="secondary"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        onKeyDown={handleEnterAsTab}
                      />
                    )}
                  />
                  <BirthNumberFields
                    prefix="driver"
                    control={control}
                    birthNumber={driverBirthNumber}
                    onSearch={() => {}}
                    showSearchIcon={false}
                  />
                </Stack>
              )}
            </Box>
          </Grid>

          {/* ── Kontaktní osoba ── */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Typography variant="h6">Kontaktní osoba</Typography>
            <Box mt={2}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={contactSameAsOwner || 'none'}
                  onChange={(e) => {
                    const newValue = e.target.value as SameAsValue;
                    onChangeContactSameAsOwner?.(newValue);
                  }}
                >
                  {!shouldHideUserOptionForContact && (
                    <FormControlLabel value="user" control={<Radio />} label="Stejný jako majitel" />
                  )}
                  {!shouldHideOperatorOptionForContact && (
                    <FormControlLabel value="operator" control={<Radio />} label="Stejný jako provozovatel" />
                  )}
                  {!shouldHideDriverOptionForContact && (
                    <FormControlLabel value="driver" control={<Radio />} label="Stejný jako řidič" />
                  )}
                  <FormControlLabel value="none" control={<Radio />} label="Jiný" />
                </RadioGroup>
              </FormControl>

              {contactSameAsOwner === 'none' && (
                <Stack gap={2} mt={2}>
                  <Grid container spacing={2}>
                    <Grid size={3}>
                      <Controller
                        control={control}
                        name="contactPerson.degree"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            label="Titul"
                            color="secondary"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            onKeyDown={handleEnterAsTab}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={9}>
                      <Controller
                        control={control}
                        name="contactPerson.name"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            label="Jméno"
                            color="secondary"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            onKeyDown={handleEnterAsTab}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Controller
                    control={control}
                    name="contactPerson.surname"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label="Příjmení"
                        color="secondary"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        onKeyDown={handleEnterAsTab}
                      />
                    )}
                  />
                  <BirthNumberFields
                    prefix="contactPerson"
                    control={control}
                    birthNumber={contactBirthNumber}
                    onSearch={() => {}}
                    showBirthDate={false}
                    showSearchIcon={false}
                  />
                  <Controller
                    control={control}
                    name="contactPerson.phone"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label="Telefon"
                        color="secondary"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        onKeyDown={handleEnterAsTab}
                      />
                    )}
                  />
                </Stack>
              )}
            </Box>
          </Grid>

          {/* ── Notifikace ── */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Typography variant="h6">Notifikace</Typography>
            <Stack spacing={2} mt={2}>
              <Grid container spacing={2}>
                <Grid size={9}>
                  <Controller
                    control={control}
                    name="notification.email"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        value={value ?? ''}
                        onChange={onChange}
                        type="email"
                        label="E-mail"
                        color="secondary"
                        fullWidth
                        helperText="Slouží pro zaslání informačního emailu"
                        InputLabelProps={{ shrink: true }}
                        onKeyDown={handleEnterAsTab}
                      />
                    )}
                  />
                </Grid>
                <Grid size={3}>
                  <Tooltip title="Vyhledat v systému">
                    <Button
                      variant="contained"
                      color="info"
                      onClick={() => handleGetAccountEmail?.(notificationEmail)}
                      disabled={!notificationEmail}
                      sx={{ height: '56px' }}
                    >
                      <SearchLG />
                    </Button>
                  </Tooltip>
                </Grid>
              </Grid>
              <Controller
                control={control}
                name="notification.phone"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value ?? ''}
                    onChange={onChange}
                    label="Telefon"
                    color="secondary"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onKeyDown={handleEnterAsTab}
                  />
                )}
              />
              {accountResult && typeof accountResult === 'object' && accountResult.email && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {` Tento e-mail: ${accountResult.email} je přiřazen existujícímu účtu. Nehoda bude
                      zobrazena v aplikaci.`}
                </Alert>
              )}
              {accountResult === false && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3
                  }}
                >
                  <Stack spacing={1}>
                    <Box>Tento e-mail není přiřazen účtu, chcete účet vytvořit?</Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => setAddNewAccountDialogOpen(true)}
                    >
                      Vytvořit účet
                    </Button>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </Grid>
        </Grid>
      </form>
      <AddNewAccountDialog
        open={addNewAccountDialogOpen}
        onClose={() => setAddNewAccountDialogOpen(false)}
        onSubmit={handleCreateAccount}
        isLoading={isCreatingAccount}
        email={notificationEmail}
        phone={notificationPhone}
        userData={
          userType !== AccidentUserType.Company
            ? {
                degree: watch('user.degree'),
                name: watch('user.name'),
                surname: watch('user.surname')
              }
            : undefined
        }
        operatorData={
          !operatorSameAsOwner && operatorType !== AccidentUserType.Company
            ? {
                degree: watch('operator.degree'),
                name: watch('operator.name'),
                surname: watch('operator.surname')
              }
            : undefined
        }
      />
    </BoxWithHeader>
  );
};
